import { DOCUMENT } from '@angular/common';
import { DestroyRef, Inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { getComponent, L10n } from '@syncfusion/ej2-base';

/**
 * Culture the Syncfusion grid strings are registered under.
 *
 * Every `<ejs-grid>` in this app is rendered without an explicit `[locale]`, so each one
 * builds its localization object against `defaultCulture`, which is `'en-US'` unless
 * `setCulture()` is called. Registering the translated strings under that same culture is
 * what lets a single `L10n.load()` reach all of them.
 *
 * `setCulture()` is deliberately NOT called: it also switches the culture used by
 * `Internationalization` for date and number formatting, and no CLDR data is loaded for the
 * ten Indic locales this app ships — dates and numbers would fall back or throw. Loading
 * translated text under `'en-US'` changes the labels without touching formatting.
 */
const SYNCFUSION_DEFAULT_CULTURE = 'en-US';

/**
 * Maps a Syncfusion grid localization key to the ngx-translate key holding its translation.
 *
 * The Syncfusion keys are its own wire constants (see the `grid` defaults in
 * `@syncfusion/ej2-grids`), so they must be spelled exactly — `Columnchooser` really does
 * carry the lowercase "c", and `autoFit`/`autoFitAll` are the camelCase variants the column
 * menu reads (the `AutoFit`/`AutoFitAll` spellings feed the toolbar).
 */
const GRID_LOCALE_KEYS: Readonly<Record<string, string>> = {
  // Empty state — the row a grid renders in place of data when its dataSource is empty.
  // Syncfusion writes this string directly as the cell's innerHTML (see `renderEmptyRow`
  // in @syncfusion/ej2-grids), so only grids WITHOUT an `#emptyRecordTemplate` read it;
  // the ~80 grids that declare that template keep rendering their own translated markup.
  EmptyRecord: 'components.grid.no_records_to_display',
  // Column-menu items — the menu that opens from a column header's ≡ icon.
  SortAscending: 'components.grid.sort_ascending',
  SortDescending: 'components.grid.sort_descending',
  Columnchooser: 'components.grid.columns',
  FilterMenu: 'components.grid.filter',
  Group: 'components.grid.group_by_this_column',
  Ungroup: 'components.grid.ungroup_by_this_column',
  autoFit: 'components.grid.autofit_this_column',
  autoFitAll: 'components.grid.autofit_all_columns',
  AutoFit: 'components.grid.autofit_this_column',
  AutoFitAll: 'components.grid.autofit_all_columns',
  // Chrome of the column chooser that the "Columns" item opens as a submenu.
  ChooseColumns: 'components.grid.choose_column',
  SearchColumns: 'components.grid.search_columns',
  Matchs: 'components.grid.no_matches_found',
  // Filter popup's entry for empty cells (null / undefined / ''). Syncfusion also compares the
  // rendered label and the search-box text against this same localized string, so it stays one key.
  Blanks: 'components.grid.blanks',
  OKButton: 'components.grid.ok_button',
  CancelButton: 'components.grid.cancel_button'
};

/**
 * Maps a Syncfusion dropdown localization key to the ngx-translate key holding its translation.
 *
 * These are the two strings a dropdown popup shows instead of a list: the empty state
 * ("No records found") and the data-failure state ("Request failed"). Like the grid's
 * `EmptyRecord`, they are written straight into the popup by the component and no template
 * or `translate` pipe can reach them.
 */
const DROPDOWN_LOCALE_KEYS: Readonly<Record<string, string>> = {
  noRecordsTemplate: 'components.dropdowns.no_records_found',
  actionFailureTemplate: 'components.dropdowns.request_failed'
};

/**
 * Syncfusion localization module names the dropdown strings must be registered under.
 *
 * `DropDownBase.l10nUpdate()` first looks for a catalogue under the component's own locale
 * name (`drop-down-list`, `multi-select`, …) and falls back to the shared `dropdowns` module
 * when that lookup comes back empty — so registering under `dropdowns` alone reaches
 * `ejs-dropdownlist`, `ejs-multiselect`, `ejs-combobox` and `ejs-autocomplete`.
 * `ejs-dropdowntree` is the exception: it resolves against `drop-down-tree` with no fallback,
 * so it needs its own entry. Registering only these two names leaves every other component's
 * catalogue untouched, and any key not listed in `DROPDOWN_LOCALE_KEYS` still resolves to the
 * defaults Syncfusion passes in (`overflowCountTemplate`, `totalCountTemplate`, …).
 */
const DROPDOWN_LOCALE_MODULES: readonly string[] = ['dropdowns', 'drop-down-tree'];

/**
 * Maps a Syncfusion pager localization key to the ngx-translate key holding its translation.
 *
 * The pager below every grid renders its own chrome — the record count, the page-size label and
 * the navigation tooltips — from the `pager` L10n catalogue, so no template reaches it. Only the
 * lowercase spellings are listed: `Pager.getLocalizedLabel()` reads those, while the
 * `CurrentPageInfo`/`PagerDropDown` capitalised twins in Syncfusion's defaults are unused.
 *
 * `currentPageInfo`, `totalItemsInfo` and `totalItemInfo` carry Syncfusion's own `{0}`/`{1}`
 * format tokens. Those must survive into every translation — ngx-translate only interpolates
 * `{{double}}` braces, so single braces pass through untouched, but a translator dropping or
 * reordering them changes what the pager prints.
 *
 * `All` reuses the app-wide `all` key rather than getting a pager-specific duplicate.
 */
const PAGER_LOCALE_KEYS: Readonly<Record<string, string>> = {
  currentPageInfo: 'components.pager.current_page_info',
  totalItemsInfo: 'components.pager.total_items_info',
  totalItemInfo: 'components.pager.total_item_info',
  pagerDropDown: 'components.pager.items_per_page',
  pagerAllDropDown: 'components.pager.items',
  All: 'all',
  firstPageTooltip: 'components.pager.first_page_tooltip',
  lastPageTooltip: 'components.pager.last_page_tooltip',
  nextPageTooltip: 'components.pager.next_page_tooltip',
  previousPageTooltip: 'components.pager.previous_page_tooltip',
  nextPagerTooltip: 'components.pager.next_pager_tooltip',
  previousPagerTooltip: 'components.pager.previous_pager_tooltip'
};

/**
 * The slice of a Syncfusion `Grid` instance this service touches, and nothing more.
 *
 * `GridComponent` is deliberately NOT imported: this service runs at application start-up and
 * importing the grid package here would pull it into the initial bundle just to read a few
 * members off an already-rendered instance. Declaring the shape structurally keeps the `any`
 * out of the call site and makes the coupling surface explicit — these four members are the
 * entire contract this service depends on, so a Syncfusion upgrade only has to be checked
 * against this block.
 *
 * Every member is optional because they are read off a live widget whose modules are injected
 * per grid: `pagerModule` is absent on an unpaged grid, and `emptyRecordTemplate` is only set
 * by the ~80 grids that declare one.
 */
interface SyncfusionGridInstance {
  /** Pager module, present only when the grid is paged. */
  pagerModule?: {
    /** The pager widget itself, which owns the cached chrome strings. */
    pagerObj?: {
      /** Rebuilds the pager's record count, page-size label and tooltips from the catalogue. */
      refresh?: () => void;
    };
  };
  /** Set when the grid declares an `#emptyRecordTemplate`, whose markup Angular owns. */
  emptyRecordTemplate?: unknown;
  /** Full re-render of the grid. */
  refresh?: () => void;
}

/** * Keeps Syncfusion's own built-in strings in step with the application language.
 *
 * Syncfusion renders the column menu ("Sort Ascending", "Columns", …), a grid's empty row
 * ("No records to display"), the pager below it ("Items per page", "{0} of {1} pages") and a
 * dropdown popup's empty state ("No records found") from its internal `L10n` catalogue, not from
 * the component template, so no `translate` pipe can reach those labels and they stay English in
 * every locale. This service copies the translations out of the ngx-translate JSON files into
 * that catalogue, once at start-up and again on every language change.
 *
 * It works for every component at once — the 200+ raw `<ejs-grid>` instances and the 800+
 * `ejs-dropdownlist` / `ejs-multiselect` / `ejs-dropdowntree` instances — without touching a
 * single component, because `L10n.load()` deep-merges INTO the existing culture object rather
 * than replacing it. Each component's localization object holds a live reference to that
 * object, so a language switch is picked up with no page reload.
 *
 * One caveat on timing: a dropdown builds its popup on every open and the grid column menu
 * re-resolves each item's text on every open, so both show the new language immediately. A
 * grid's empty row and its pager are written once and cached, so `repaintGridChrome()` repaints
 * those on the rendered grids rather than leaving them a language behind.
 *
 * @example
 *   // Once, during application start-up:
 *   this.syncfusionGridLocale.init();
 */
@Injectable({
  providedIn: 'root'
})
export class SyncfusionGridLocaleService {
  /** Guards against a second `init()` registering a duplicate language subscription. */
  private initialized = false;

  /**
   * Handle of the deferred {@link repaintGridChrome} pass, or `undefined` when none is pending.
   *
   * Held so a language change arriving before the previous pass has run cancels it rather than
   * queueing a second one. The pass walks every rendered `.e-grid` and refreshes each pager, so N
   * switches in quick succession would otherwise repeat that whole-page work N times to paint the
   * one language that ends up winning. The surviving callback still paints the latest language,
   * because it reads the catalogue when it runs rather than when it was scheduled.
   */
  private pendingRepaint?: ReturnType<typeof setTimeout>;

  /**
   * @param translate - Source of the translated strings, and of the language-change stream.
   * @param document - Injected DOM root, used to find grids that are currently showing an
   *   empty row. Injected via the `DOCUMENT` token rather than touching the global `document`
   *   so the service stays compatible with SSR and platform boundaries.
   * @param destroyRef - Lifetime of the root injector, used to unsubscribe the language stream.
   */
  constructor(
    private translate: TranslateService,
    @Inject(DOCUMENT) private document: Document,
    private destroyRef: DestroyRef
  ) {}

  /**
   * Publishes the current language's grid strings and keeps them updated.
   *
   * Safe to call more than once — only the first call takes effect, so a second host cannot
   * leave two subscriptions pushing the same values. The subscription is bound to the root
   * injector's `DestroyRef`, which in practice means the application's lifetime — the same span
   * the catalogue it maintains is needed for — but leaves no manually managed subscription behind.
   *
   * @returns {void} No return value.
   */
  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.applySyncfusionLocale();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Order matters: the catalogue has to hold the new language before any grid re-renders.
        this.applySyncfusionLocale();
        this.repaintGridChrome();
      });
  }

  /**
   * Repaints the cached chrome of every grid that is on screen: its pager, and its empty row.
   *
   * Syncfusion writes both once and caches them, so neither `L10n.load()` nor a `translate` pipe
   * repaints a grid the user is already looking at — it keeps the previous language until
   * something re-renders it.
   *
   * **Pager.** Applies to every paged grid, with rows or without: `pagerObj.refresh()` rebuilds
   * the record count, the page-size label and the tooltips from the catalogue. No grid in this
   * app declares a `pagerTemplate`, so this takes Syncfusion's plain re-render path, and the
   * pager holds no state of its own — the current page and page size live on the grid.
   *
   * **Empty row**, handled in two cases:
   * - **No `#emptyRecordTemplate`** — the cell is a plain `<td>` whose text Syncfusion wrote
   *   from the L10n catalogue, so the new translation is written straight onto it. No refresh,
   *   no re-bind, and nothing else in the grid moves.
   * - **With an `#emptyRecordTemplate`** — the cell holds Angular-rendered markup that cannot
   *   be rewritten safely, so the grid is refreshed. That is the same `grid.refresh()` the
   *   components carrying their own language listener already call for this case, and it is
   *   cheap here because the grid is empty by definition and every grid in this app is bound
   *   to a local array (no remote `DataManager`), so no request is issued.
   *
   * A grid that has rows keeps its rows and headers untouched — headers stay a separate concern,
   * owned by the per-component `[headerText]` listeners.
   *
   * Deferred a tick because `onLangChange` fires *before* the change-detection pass that
   * re-evaluates the `translate` pipes an empty-record template depends on. Only one pass is ever
   * pending: a language change arriving while one is still queued replaces it rather than adding
   * to it (see {@link pendingRepaint}).
   *
   * @returns {void} No return value.
   */
  private repaintGridChrome(): void {
    clearTimeout(this.pendingRepaint);
    this.pendingRepaint = setTimeout(() => {
      this.pendingRepaint = undefined;
      const translationKey = GRID_LOCALE_KEYS['EmptyRecord'];
      const emptyText = this.translate.instant(translationKey);
      const hasText = !!emptyText && emptyText !== translationKey;

      this.document.querySelectorAll<HTMLElement>('.e-grid').forEach((element) => {
        const grid = getComponent<SyncfusionGridInstance>(element, 'grid');
        if (!grid) {
          return;
        }

        grid.pagerModule?.pagerObj?.refresh?.();

        const cell = element.querySelector<HTMLElement>('tr.e-emptyrow > td');
        // Skip a grid that has rows, and skip a cell belonging to a nested grid — that inner
        // grid gets its own turn in this loop and must not be repainted twice.
        if (!cell || cell.closest('.e-grid') !== element) {
          return;
        }

        if (grid.emptyRecordTemplate) {
          grid.refresh?.();
        } else if (hasText) {
          cell.textContent = emptyText;
        }
      });
    });
  }

  /**
   * Writes the active language's values into Syncfusion's grid, pager and dropdown catalogues.
   *
   * The dropdown strings are registered under every module name in
   * `DROPDOWN_LOCALE_MODULES` so one load reaches the whole dropdown family.
   *
   * @returns {void} No return value.
   */
  private applySyncfusionLocale(): void {
    const gridStrings = this.resolve(GRID_LOCALE_KEYS);
    const dropdownStrings = this.resolve(DROPDOWN_LOCALE_KEYS);

    const catalogue: Record<string, Record<string, string>> = {
      grid: gridStrings,
      pager: this.resolve(PAGER_LOCALE_KEYS)
    };
    for (const moduleName of DROPDOWN_LOCALE_MODULES) {
      catalogue[moduleName] = dropdownStrings;
    }

    L10n.load({ [SYNCFUSION_DEFAULT_CULTURE]: catalogue });
  }

  /**
   * Translates a Syncfusion-key → translation-key map into a Syncfusion string catalogue.
   *
   * A key with no translation registered is skipped rather than written, so Syncfusion keeps
   * its own English default instead of displaying a raw translation key — `instant()` echoes
   * the key back on a miss, which is how a miss is detected here.
   *
   * @param {Readonly<Record<string, string>>} localeKeys - Syncfusion key → ngx-translate key.
   * @returns {Record<string, string>} Syncfusion key → translated text, misses omitted.
   */
  private resolve(localeKeys: Readonly<Record<string, string>>): Record<string, string> {
    const strings: Record<string, string> = {};

    for (const [syncfusionKey, translationKey] of Object.entries(localeKeys)) {
      const translated = this.translate.instant(translationKey);
      if (translated && translated !== translationKey) {
        strings[syncfusionKey] = translated;
      }
    }

    return strings;
  }
}
